import React from "react";
import { Button, Input, Modal, FileInput, DatePicker, Toast, TeamSearch, ButtonGroup, Checkbox, ErrorMsg, Chips, Select } from "../../";
import { useState } from "react";
import { apis, post } from "../../../Utils/axios";
import moment from "moment";
import { useAdminPermission } from "../../../Hooks/useAdminPermission";
import { FaPlus } from "react-icons/fa";
import { validateEmail, validatePhoneNumber } from "Utils/utils";
import { COUNTRIES } from "Utils/constants";

function AddEmployee({ teamId, onReload = () => {}, btnLabel = "+ Add User", btnClass = "", btnVariant = "primary" }) {
	const { checkAdminPermission } = useAdminPermission();
	const [NewEmployee, setNewEmployee] = useState();
	const [SendInvite, setSendInvite] = useState(true);
	const [Emails, setEmails] = useState([]);

	const initial = {
		fName: "",
		lName: "",
		email: "",
		gender: {
			label: "Male",
			value: "MALE",
		},
		phone: "",
		photo: "",
		team: {},
		country: {},
		dob: moment().subtract(18, "years").valueOf(),
	};
	const [Values, setValues] = useState(initial);

	const [Loader, setLoader] = useState();
	const [Error, setError] = useState();

	const addEmail = (email) => {
		if (!Values.email) {
			return setError("Please enter email address first");
		}
		if (!validateEmail(Values.email)) {
			return setError("Please enter valid email address");
		}
		setEmails((prev) => [...prev, Values.email.split(",")[0].trim()]);
		setValues((prev) => ({ ...prev, email: "" }));
	};

	const onSubmit = async () => {
		if (!teamId && !Values.team.value) {
			return setError("Team is required for adding user");
		}
		if (!Values.gender) {
			return setError("Please select gender");
		}
		if (!Values.email) {
			return setError("Please enter email address");
		}
		if (!Values.dob) {
			return setError("Please select date of birth");
		}
		setError();
		setLoader(true);
		if (!(await validatePhoneNumber(Values.phone, Values.country.value))) {
			setLoader(false);
			return Toast.error("Please enter valid phone number");
		}

		post(
			apis.addUser,
			{
				...Values,
				dob: moment(Values.dob).toDate(),
				phoneCode: Values.country?.value,
				gender: Values.gender?.value,
				teamId: teamId || Values.team.value,
				photo: Values.photo?.split("?")[0],
			},
			(r, e) => {
				if (r) {
					Toast.success("Verification link sent to user");
					setNewEmployee(false);
					setLoader(false);
					onReload();
					setValues(initial);
				} else if (e) {
					if (e.response.status === 409) {
						setError("User already exists, please use different email address");
					} else {
						Toast.handleError(e);
					}
					setLoader(false);
				}
			}
		);
	};

	const sendInvite = () => {
		let emails = [...Emails];
		if (!emails.length) {
			emails = [Values.email];
		}
		setError();
		setLoader(true);
		post(apis.sendInvite, { emails }, (r, e) => {
			if (r) {
				Toast.success("Invite sent !!");
				setNewEmployee(false);
				setLoader(false);
				onReload();
				setValues(initial);
			} else if (e) {
				if (e.response.status === 409) {
					setError("User already exists, please use different email address");
				} else {
					Toast.handleError(e);
				}
				setLoader(false);
			}
		});
	};

	if (!checkAdminPermission("EDIT_USERS")) {
		return null;
	}

	return (
		<>
			<Button onClick={() => setNewEmployee(true)} variant={btnVariant} className={btnClass}>
				{btnLabel}
			</Button>
			<Modal
				open={NewEmployee}
				onClose={() => {
					setError();
					setNewEmployee();
					setValues(initial);
					setSendInvite(true);
					setEmails([]);
				}}
			>
				<div className="p-6">
					<h1>Invite User</h1>
					<div className="mt-2 text-label">You can invite users to join Travel Wings by sending them invitation on their email address</div>
					<div className="flex flex-col mt-4 gap-y-4">
						<div className="flex items-end gap-2">
							<Input type="email" required value={Values.email} onChange={(email) => setValues((prev) => ({ ...prev, email }))} label="Email Address" placeholder="Email" className="flex-1" />
							{SendInvite && (
								<Button className="gap-2" variant="outlined" onClick={addEmail}>
									<FaPlus /> More
								</Button>
							)}
						</div>
						{SendInvite && <Chips className="flex-wrap gap-2" options={Emails.map((email) => ({ label: email, value: email }))} onDelete={(email) => setEmails((prev) => prev.filter((e) => e !== email.value))} />}
						{!SendInvite && (
							<>
								{!teamId && <TeamSearch label="Team this user belongs to" placeholder="Search Teams" onSelect={(team) => setValues((prev) => ({ ...prev, team }))} selected={Values.team} />}
								<div className="grid grid-cols-2 gap-x-4">
									<Input noNumbers required value={Values.fName} onChange={(fName) => setValues((prev) => ({ ...prev, fName }))} label="First Name" placeholder="First Name" />
									<Input noNumbers required value={Values.lName} onChange={(lName) => setValues((prev) => ({ ...prev, lName }))} label="Last Name" placeholder="Last Name" />
								</div>
								<DatePicker backDateSelection={true} maxDate={moment().subtract(18, "years").valueOf()} date={Values.dob} setDate={(dob) => setValues((prev) => ({ ...prev, dob }))} label="Date of Birth" />
								<label>Phone Number</label>
								<div className="flex gap-2">
									<Select className="max-w-min" placeholder="Code" selected={Values.country} onChange={(country) => setValues((prev) => ({ ...prev, country }))} required label="" options={COUNTRIES} />
									<Input className="flex-1" type="number" required value={Values.phone} onChange={(phone) => setValues((prev) => ({ ...prev, phone }))} placeholder="Phone" />
								</div>
								<div className="grid grid-cols-2 gap-x-4">
									<FileInput file={Values.photo} onUpload={(obj) => setValues((prev) => ({ ...prev, photo: obj.file.key }))} label="Profile Photo" />
									<div>
										<label className="block mb-2">Gender</label>
										<ButtonGroup
											selected={Values.gender}
											onClick={(gender) => setValues((prev) => ({ ...prev, gender }))}
											options={[
												{
													label: "Male",
													value: "MALE",
												},
												{
													label: "Female",
													value: "FEMALE",
												},
											]}
										/>
									</div>
								</div>
							</>
						)}
						<Checkbox checked={SendInvite} onChange={setSendInvite} label="Add user by sending invitation on email" />
						<ErrorMsg e={Error} />
						{SendInvite ? (
							<Button loading={Loader} onClick={sendInvite} className="w-full mt-4">
								Send Invite
							</Button>
						) : (
							<Button loading={Loader} onClick={onSubmit} className="w-full mt-4">
								Create Employee
							</Button>
						)}
					</div>
				</div>
			</Modal>
		</>
	);
}

export default AddEmployee;
